/* global UI_THEME */
import { $, util } from '@glu/core';
import locale from '@glu/locale';
import deepClone from 'clone-deep';
import baseDeepEqual from 'deep-equal';

Object.assign(util, {
  goBack(history) {
    history = history || window.history; // eslint-disable-line
    history.go(-1);
  },
  parseQueryString(queryString) {
    const params = {};
    if (!queryString) {
      return params;
    }
    const paramStrings = decodeURIComponent(queryString.replace(/\+/g, ' ')).split(/&/g);
    const object = {};
    const paramObjects = paramStrings.map((el) => {
      const aux = el.split('=');
      let val;
      if (aux.length >= 1) {
        if (aux.length === 2) {
          [, val] = aux;
        }
        object[aux[0]] = val;
      }
      return object;
    });
    paramObjects.forEach((paramObject) => {
      util.extend(params, paramObject);
    });
    return params;
  },
  inRect(x, y, rect) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  },
  inObject(x, y, object) {
    const rects = object.getClientRects();
    for (let i = rects.length; i--;) { // eslint-disable-line
      if (this.inRect(x, y, rects[i])) {
        return true;
      }
    }
    return false;
  },
  getTextNodes(node, x, y) {
    if (!this.inObject(x, y, node)) {
      return [];
    }

    let result = [];
    node = node.firstChild; // eslint-disable-line
    while (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        result.push(node);
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        result = result.concat(this.getTextNodes(node, x, y));
      }

      node = node.nextSibling; // eslint-disable-line
    }

    return result;
  },
  getRangeFromPoint(x, y) {
    const element = document.elementFromPoint(x, y);
    const nodes = this.getTextNodes(element, x, y);
    let range;
    if (!nodes.length) {
      const newNode = document.createTextNode(' ');
      element.appendChild(newNode);
      range = document.createRange();
      range.setStart(newNode, 0);
      range.setEnd(newNode, 1);
      return range;
    }
    for (let i = nodes.length; i--;) { // eslint-disable-line
      const textNode = nodes[i];
      const text = textNode.nodeValue;

      range = document.createRange();
      range.setStart(textNode, 0);
      range.setEnd(textNode, text.length);

      if (!this.inObject(x, y, range)) {
        // eslint-disable-next-line no-continue
        continue;
      }

      for (let j = text.length; j--;) { // eslint-disable-line
        if (text.charCodeAt(j) <= 32) {
          // eslint-disable-next-line no-continue
          continue;
        }

        range = document.createRange();
        range.setStart(textNode, j);
        range.setEnd(textNode, j + 1);

        if (this.inObject(x, y, range)) {
          range.setEnd(textNode, j);
          return range;
        }
      }
    }

    const node = nodes[nodes.length - 1];
    range = document.createRange();
    range.setStart(node, 0);
    range.setEnd(node, 1);

    return range;
  },
  stripHTML(rawHTML) {
    return util.composeForwards(convertEscapedTagsToPlainText, escapeHTMLInString)(rawHTML); // eslint-disable-line

    function convertEscapedTagsToPlainText(html) {
      return html.replace(/&gt;/gi, '>').replace(/&lt;/gi, '<');
    }

    function escapeHTMLInString(html) {
      const plainTextParsed = document.createElement('div');
      plainTextParsed.textContent = html;
      return plainTextParsed.innerHTML;
    }
  },
  composeForwards(...args) {
    const argsArray = [].slice.call(args);
    return util.compose.apply(null, argsArray.reverse());
  },
  /**
     * This concatenates the messages in a standard response in the style of
     * {
     *     error: {
     *         generic: []
     *     },
     *     information: {},
     *     warning: {}
     * }
     */
  concatenateStandardResponse(response, prop) {
    if (!response[prop]) {
      return null;
    }
    return Object.keys(response[prop]).reduce((fullString, group) => `${fullString + response[prop][group].join('. ')}. `, '');
  },
  getMessagesIfExist(response, prop) {
    if (!response) {
      return null;
    }
    const responseJSON = response.responseJSON || response;
    return util.concatenateStandardResponse(responseJSON, prop);
  },
  navHeight() {
    const $layoutManager = $('.layout-manager');
    const navHeight = $layoutManager.children('#nav').height() || 50;
    // default height of the nav bar
    const $pageHeadline = $layoutManager.find('.page-headline');
    const pageHeadlineHeight = $pageHeadline.height() || 49;
    // default height of the page-headline
    const $colorBar = $pageHeadline.find('.colorbar');
    const colorBarHeight = $colorBar.height() || 8; // default height of the color-bar

    return navHeight + pageHeadlineHeight + colorBarHeight;
  },
  standardStickingPoint() {
    return this.navHeight() + 20;
  },
  /**
   * Will convert provided string to title cased string
   * and skip small words such a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|vs?\.?|via
   * @param string
   * @returns {string}
   */
  toTitleCase: (string = '') => {
    const smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|vs?\.?|via)$/i;

    return string.replace(/[A-Za-z0-9\u00C0-\u00FF]+[^\s-/]*/g, (match, index, title) => {
      if (index > 0 && index + match.length !== title.length
        && match.search(smallWords) > -1 && title.charAt(index - 2) !== ':'
        && (title.charAt(index + match.length) !== '-' || title.charAt(index - 1) === '-')
        && title.charAt(index - 1).search(/[^\s-]/) < 0) {
        return match.toLowerCase();
      }
      if (match.substr(1).search(/[A-Z]|\../) > -1) {
        return match;
      }
      return match.charAt(0).toUpperCase() + match.substr(1);
    });
  },
  insertAfter: (newNode, referenceNode) => {
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
  },

  locationReload: (flag = true) => (
    window.location.reload(flag)
  ),
  /**
   * This generates the window's title for a given page.
   * This will try to pull a title property from the determined page object. If that fails, it will
   * fall back to using the heading instead.
   * @param {String} localePageRef - The page object within the internationalisation.
   */
  generateTitle: (localePageRef) => {
    const localePage = locale.get(localePageRef);
    document.title = `PTX - ${localePage.indexOf('?') !== 0 ? localePage : localePageRef }`;
  },

  /**
   * Keep page-headline to have sam width as main content.
   */
  updateWidth() {
    const $mainContent = document.querySelector('.main-content') || document.querySelector('[data-qa="main-content"]');
    const $pageHeadline = document.querySelector('.page-headline') || document.querySelector('[data-qa="page-title"]');
    if (!$mainContent) {
      return;
    }
    if ($pageHeadline) {
      $pageHeadline.style.width = `${$mainContent.offsetWidth}px`;
    }
  },

  getBulkActionsMessage({ responses }) {
    const messages = responses.map((response) => { // eslint-disable-line
      if (response.error?.generic) {
        return {
          type: 'danger',
          message: response.error.generic.join(' ')
        };
      }
      if (response.status === 200) {
        return {
          type: 'success',
          message: 'Succeeded'
        };
      }
    });

    const groupedMessages = messages.reduce((acc, msg) => {
      const obj = acc[msg.type].find(accMessage => accMessage.message === msg.message);
      if (!obj) {
        acc[msg.type].push({
          message: msg.message,
          count: 1
        });
      } else {
        obj.count += 1;
      }
      return acc;
    }, { danger: [], success: [] });
    const successMessages = groupedMessages.success.reduce((acc, msg) => `${acc}${acc === '' ? '' : '\r\n'}${locale.get('succeeded', msg.count)}`, '');
    const errorMessages = groupedMessages.danger.reduce((acc, msg) => `${acc}${acc === '' ? '' : '\r\n'}${locale.get('failedWithMessage', msg.count, msg.message)}`, '');
    const singleMessage = successMessages && errorMessages ? `${successMessages}\r\n${errorMessages}` : `${successMessages}${errorMessages}`;

    return {
      type: groupedMessages.danger.length ? 'danger' : 'success',
      singleMessage
    };
  },

  deepClone,

  deepEqual(a, b, options = { strict: true }) {
    return baseDeepEqual(a, b, options);
  },

  isProduction() {
    return process.env.NODE_ENV === 'production';
  },

  // only for IE, will show document print preview. Use article from:
  // http://forums.asp.net/t/1095049.aspx?access+print+preview+option+of+internet+explorer
  // eslint-disable-next-line class-methods-use-this
  showPrintPreview() {
    const objPrintPreview = '<object id="printPrev" width="0" height="0" classid="CLSID:8856F961-340A-11D0-A96B-00C04FD705A2"></object>';
    document.body.insertAdjacentHTML('beforeEnd', objPrintPreview);

    // should check if Microsoft scripting is available
    // http://connect.microsoft.com/IE/feedbackdetail/view/928561/execwb-member-not-found
    if (document.printPrev.execWB) {
      document.printPrev.execWB(7, 1);
    } else {
      window.print();
    }
    document.printPrev.outerHTML = '';
  },
  isGluAsh() {
    return UI_THEME === 'glu-ash';
  }
});
export default util;
